import { useMutation, useQuery } from "@tanstack/react-query";
import type { InfluencerData } from "@/features/influencer-monitoring/store/influencerStore";
import {
  linksFromRow,
  fetchInfluencerByLinks,
  requestInfluencer,
} from "./influencer.api";
import { useInfluencersApiToken } from "@/features/auth/hooks/useApiToken";
import type { RequestInfluencerPayload } from "../types/influencer.types";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errors";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { extractPlatformAndUsernameFromUrl } from "../utils/urlUtils";
import type { LinkValidationResult } from "../utils/linkValidation";
import { normalizeLink } from "../utils/linkValidation";

const mapPlatformForApi = (platform: string) => {
  const p = platform.toLowerCase();
  if (p === "facebook") return "fb";
  if (p === "twitter") return "x";
  if (p === "youtube") return "yt";
  return p;
};

export interface UseMatchedInfluencersOptions {
  hasLaunched: boolean;
  linkValidation: LinkValidationResult;
}

export function useMatchedInfluencers(
  rows: InfluencerData[],
  options: UseMatchedInfluencersOptions,
) {
  const { hasLaunched, linkValidation } = options;
  const { apiToken } = useInfluencersApiToken();
  const { user } = useAuthStore();
  const linksByRow = rows.map(linksFromRow);

  const hasLinks = linksByRow.some((arr) => arr.length > 0);
  const enabled = hasLinks && hasLaunched;

  // Key only by hasLaunched so adding/editing rows does not trigger fetch; refetch runs only when Launch is clicked
  return useQuery({
    queryKey: ["matchedInfluencers", hasLaunched],
    enabled,
    queryFn: async () => {
      const { invalidLinks, duplicateLinks } = linkValidation;
      const requestedInThisRun = new Set<string>();

      const results = await Promise.all(
        linksByRow.map(async (links, rowIndex) => {
          if (links.length === 0) {
            return { rowIndex, links, data: null };
          }
          const data = await fetchInfluencerByLinks(links, apiToken ?? "");

          // Auto-request only for valid, non-duplicate links that have no match
          if (
            data &&
            Array.isArray(data) &&
            data.length === 0 &&
            links.length > 0
          ) {
            for (const link of links) {
              const trimmed = link.trim();
              const normalized = normalizeLink(link);
              if (invalidLinks.has(trimmed) || duplicateLinks.has(normalized))
                continue;
              if (requestedInThisRun.has(normalized)) continue;

              const platformData = extractPlatformAndUsernameFromUrl(link);
              if (platformData && user?.mongo_id) {
                try {
                  requestedInThisRun.add(normalized);
                  await requestInfluencer(
                    {
                      platform: mapPlatformForApi(platformData.platform) as any,
                      user_id: user.mongo_id,
                      url: link,
                      for_fluxy: false,
                    },
                    apiToken ?? "",
                  );
                  toast.info(`Requested influencer: ${platformData.username}`, {
                    description: `Platform: ${platformData.platform}`,
                  });
                } catch (error) {
                  console.error("Failed to auto-request influencer:", error);
                  requestedInThisRun.delete(normalized);
                }
              }
            }
          }

          return { rowIndex, links, data };
        }),
      );
      return results;
    },
  });
}

export function useRequestInfluencer(payload: RequestInfluencerPayload) {
  const { apiToken } = useInfluencersApiToken();
  return useMutation({
    mutationFn: async () => {
      const mappedPayload = {
        ...payload,
        platform: mapPlatformForApi(payload.platform) as any,
      };
      return await requestInfluencer(mappedPayload, apiToken ?? "");
    },
    onSuccess: () => {
      toast.success("Influencer requested successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to request influencer", {
        description: extractErrorMessage(error),
      });
    },
  });
}
